/**
 * ProgressTracker
 *
 * A component for tracking progress through steps or towards a goal
 *
 * Primary responsibilities:
 * - Display current progress with visual indicators
 * - Show both numeric and percentage progress
 * - Support step-based and continuous progress tracking
 * - Allow toggling between compact and detailed views
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import {
  IconCheck,
  IconInfoCircle,
  IconArrowRight,
  IconArrowDown,
  IconProgressCheck
} from '@tabler/icons-react'

interface Step {
  title: string
  description?: string
  completed: boolean
}

interface ProgressTrackerProps {
  title: string
  description?: string
  steps?: Step[]
  currentValue?: number
  maxValue?: number
  showPercentage?: boolean
  className?: string
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  title,
  description,
  steps = [],
  currentValue = 0,
  maxValue = 100,
  showPercentage = true,
  className
}) => {
  const [expanded, setExpanded] = useState(false)

  const isStepBased = steps.length > 0
  const completedSteps = isStepBased ? steps.filter((step) => step.completed).length : 0
  const totalSteps = steps.length

  // Calculate progress percentage
  const progressPercentage = isStepBased
    ? totalSteps > 0
      ? Math.round((completedSteps / totalSteps) * 100)
      : 0
    : maxValue > 0
      ? Math.round((currentValue / maxValue) * 100)
      : 0

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconProgressCheck size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
          {showPercentage && (
            <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              {progressPercentage}%
            </span>
          )}
        </div>
        {isStepBased && (
          <Button variant="ghost" size="sm" onClick={toggleExpanded} className="ml-auto">
            {expanded ? <IconArrowDown size={14} /> : <IconArrowRight size={14} />}
          </Button>
        )}
      </div>

      <div className="p-4">
        {description && <p className="text-muted-foreground mb-4 text-sm">{description}</p>}

        <div className="space-y-4">
          {/* Progress bar */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-muted-foreground text-sm">
                {isStepBased
                  ? `${completedSteps}/${totalSteps} steps`
                  : `${currentValue}/${maxValue}`}
              </span>
            </div>
            <Progress value={progressPercentage} />
          </div>

          {/* Step list (if step-based) */}
          {isStepBased && expanded && (
            <div className="mt-4 space-y-2">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex items-start gap-3 rounded-md p-2 transition-colors',
                    step.completed ? 'bg-green-50 dark:bg-green-900/10' : 'hover:bg-accent/50'
                  )}
                >
                  <div
                    className={cn(
                      'flex size-5 shrink-0 items-center justify-center rounded-full',
                      step.completed ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                    )}
                  >
                    {step.completed ? (
                      <IconCheck size={12} className="text-white" />
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{index + 1}</span>
                    )}
                  </div>

                  <div className="flex-1">
                    <h4
                      className={cn(
                        'text-sm font-medium',
                        step.completed ? 'text-green-700 dark:text-green-400' : ''
                      )}
                    >
                      {step.title}
                    </h4>
                    {step.description && (
                      <p className="text-muted-foreground mt-1 text-xs">{step.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Non-step based details */}
          {!isStepBased && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="flex items-center gap-1">
                <IconInfoCircle size={14} className="text-blue-500" />
                <span>Current progress</span>
              </div>
              <span className="font-medium">
                {currentValue} of {maxValue}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
